import { useContext, useState, useEffect, useCallback } from 'react';
import AuthContext from '../../context/autenticacion/authContext';
import axios from 'axios';
import { useT } from 'i18n';

const Direcciones = () => {
  const { t } = useT();
  const authContext = useContext(AuthContext);
  const { jimf_theme } = authContext;

  const [datos, setDatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [animation, setAnimation] = useState(false);

  const isWhiteTheme = jimf_theme === 'white';

  const obtenerDirecciones = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.post('https://webservice.panther7.com/api/client/deposits/direcciones');
      
      if (response.data && response.data.data) {
        setDatos(response.data.data);
      } else {
        setDatos([]);
      }
    } catch (err) {
      console.error('Error al obtener direcciones:', err);
      setError(t('dep.dirError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    obtenerDirecciones();
  }, [obtenerDirecciones]);

  useEffect(() => {
    if (animation) {
      const timer = setTimeout(() => setAnimation(false), 50);
      return () => clearTimeout(timer);
    }
  }, [animation]);

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Error al copiar:', err);
    }
  };

  const formatAddress = (address) => {
    if (!address) return t('dep.noDisponible');
    if (address.length <= 20) return address;
    return `${address.substring(0, 10)}...${address.substring(address.length - 8)}`;
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="sr-only">{t('dep.cargandoSr')}</span>
        </div>
        <p className="mt-3 text-muted">{t('dep.dirCargando')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-5">
        <i className="tim-icons icon-alert-circle-exc" style={{ fontSize: '48px', color: '#ff5252' }} />
        <p className="mt-3 text-danger">{error}</p>
        <button 
          className="btn btn-primario btn-sm mt-2"
          onClick={() => obtenerDirecciones()}
        >
          {t('dep.reintentar')}
        </button>
      </div>
    );
  }

  if (datos.length === 0) {
    return (
      <div className="text-center py-5">
        <i className="tim-icons icon-wallet-43" style={{ fontSize: '48px', opacity: 0.5 }} />
        <p className="mt-3 text-muted">{t('dep.dirNinguna')}</p>
      </div>
    );
  }

  // Estilos comunes para la cabecera y filas
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'minmax(300px, 1fr) 120px 140px',
    gap: '1rem',
    alignItems: 'center'
  };

  return (
    <>
      {/* Cabecera */}
      <div 
        style={{
          ...gridStyle,
          padding: '12px 16px',
          backgroundColor: isWhiteTheme ? '#f0f0f0' : 'rgba(18,21,28, 0.5)',
          borderRadius: '12px',
          marginBottom: '8px',
          fontWeight: 'bold'
        }}
      >
        <div style={{ textAlign: 'left' }}>
          <i className="tim-icons icon-wallet-43 mr-1" /> {t('dep.dirColDireccion')}
        </div>
        <div style={{ textAlign: 'center' }}>
          <i className="tim-icons icon-link-72 mr-1" /> {t('dep.dirColAccion')}
        </div>
        <div style={{ textAlign: 'center' }}>
          <i className="tim-icons icon-globe mr-1" /> {t('dep.dirColNetwork')}
        </div>
      </div>

      {/* Lista de direcciones */}
      <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {datos.map((item, index) => {
          const isCopied = copiedIndex === index;
          const fullAddress = item.direccion;
          const shortAddress = formatAddress(fullAddress);

          return (
            <div
              key={item.id || index}
              style={{
                ...gridStyle,
                padding: '12px 16px',
                borderBottom: `1px solid ${isWhiteTheme ? 'rgba(0,0,0,0.05)' : 'rgba(227,168,43, 0.1)'}`,
                transition: 'all 0.3s ease',
                backgroundColor: isWhiteTheme ? '#fff' : 'transparent'
              }}
            >
              {/* Dirección */}
              <div style={{ 
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                <span 
                  style={{ 
                    fontFamily: 'monospace', 
                    fontSize: '13px',
                    cursor: 'pointer',
                    color: isWhiteTheme ? '#e3a82b' : '#e3a82b',
                    textDecoration: 'underline'
                  }}
                  title={fullAddress}
                  onClick={() => copyToClipboard(fullAddress, index)}
                >
                  <i className="tim-icons icon-files-93" style={{ fontSize: '12px', marginRight: '6px' }} />
                  {shortAddress}
                </span>
              </div>

              {/* Botón copiar */}
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={() => copyToClipboard(fullAddress, index)}
                  style={{ 
                    padding: '6px 16px',
                    fontSize: '12px',
                    borderRadius: '50px',
                    border: 'none',
                    cursor: 'pointer',
                    minWidth: '90px',
                    backgroundColor: isCopied ? '#171b24' : (isWhiteTheme ? '#e3a82b' : '#171b24'),
                    color: 'white',
                    fontWeight: '500',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (!isCopied) e.target.style.backgroundColor = '#171b24';
                  }}
                  onMouseLeave={(e) => {
                    if (!isCopied) e.target.style.backgroundColor = isWhiteTheme ? '#e3a82b' : '#171b24';
                  }}
                >
                  <i className={`tim-icons ${isCopied ? 'icon-check-2' : 'icon-files-93'}`} style={{ fontSize: '12px', marginRight: '6px' }} />
                  {isCopied ? t('dep.copiado') : t('dep.copiar')}
                </button>
              </div>

              {/* Network */}
              <div style={{ textAlign: 'center' }}>
                <span style={{ 
                  backgroundColor: '#e3a82b', 
                  borderRadius: '50px', 
                  padding: '4px 12px', 
                  fontSize: '11px',
                  color: 'white',
                  display: 'inline-block',
                  minWidth: '100px'
                }}>
                  {item.network || 'USDT (ERC-20)'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Información de ayuda */}
      <div style={{ marginTop: '20px' }}>
        <div style={{ 
          backgroundColor: isWhiteTheme ? '#e3a82b' : 'rgba(227,168,43, 0.1)',
          border: `1px solid ${isWhiteTheme ? '#e3a82b' : 'rgba(227,168,43, 0.3)'}`,
          borderRadius: '12px',
          padding: '16px',
          textAlign: 'center'
        }}>
          <i className="tim-icons icon-bulb-63" style={{ color: '#e3a82b', marginRight: '8px' }} />
          <span style={{ fontSize: '13px', color: isWhiteTheme ? '#333' : 'rgba(255,255,255,0.8)' }}>
            {t('dep.dirAyuda1')}<br />
            {t('dep.dirAyuda2')} <strong style={{ color: '#e3a82b' }}>"{t('dep.tabAvisar')}"</strong>.<br />
            {t('dep.dirAyuda3')} <strong style={{ color: '#e3a82b' }}>{t('dep.dirHoras')}</strong>.
          </span>
        </div>
      </div>
    </>
  );
};

export default Direcciones;